import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { ArrowRight, LogIn } from 'lucide-react';
import { isDesktop } from '../lib/platform';
import { useAuth } from '../hooks/useAuth';

/**
 * Welcome — first-run screen shown on desktop when firstRunComplete === false.
 *
 * Only rendered when isDesktop() is true and the ?welcome=1 query param is
 * present (set by main.js on first launch). After any successful sign-in,
 * sign-up, or activation, useAuth marks firstRunComplete in electron-store
 * so future cold starts skip straight to /tools.
 */
export default function Welcome() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    // Web users never see this — bounce to /tools.
    if (!isDesktop()) {
      navigate('/tools', { replace: true });
      return;
    }
    // Already-signed-in desktop users hit /welcome on every cold start
    // until firstRunComplete flips. Skip the screen and flip the flag so
    // the next launch doesn't bother showing it. Wait for auth to resolve
    // before deciding (user is null briefly during initial Firebase load).
    if (!authLoading && user) {
      if (window.electronAPI?.markFirstRunComplete) {
        window.electronAPI.markFirstRunComplete().catch(() => { /* non-fatal */ });
      }
      navigate('/tools', { replace: true });
    }
  }, [navigate, authLoading, user]);

  function handleCreate() {
    navigate('/login?tab=signup');
  }

  function handleSignIn() {
    navigate('/login?tab=signin');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex flex-col items-center justify-center px-6 py-12">
      {/* Hero */}
      <div className="text-center max-w-lg mb-12">
        <div
          className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center text-white font-extrabold text-2xl shadow-lg select-none"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
          aria-hidden="true"
        >
          S
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">
          Welcome to Scrivar
        </h1>
        <p className="text-base text-gray-600 leading-relaxed">
          All-in-one PDF tools for your business &mdash; merge, compress, sign,
          convert, invoice, and more. Sign in or create an account to get started.
        </p>
      </div>

      {/* CTAs */}
      <div className="flex flex-col gap-3 w-full max-w-xs mb-8">
        <button
          type="button"
          onClick={handleCreate}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-brand-blue text-white rounded-xl text-sm font-semibold hover:bg-brand-dark shadow-sm"
        >
          Create Account
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={handleSignIn}
          className="w-full flex items-center justify-center gap-2 px-5 py-3 border border-gray-200 bg-white text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-50 shadow-sm"
        >
          <LogIn className="w-4 h-4" aria-hidden="true" />
          I already have an account
        </button>
      </div>

      {/* Why an account + offline reassurance */}
      <p className="text-xs text-gray-500 text-center max-w-sm leading-relaxed mb-8">
        Your license is tied to your account so you can move it to a new computer
        or recover it anytime &mdash; and after you sign in once, Scrivar keeps
        working offline.
      </p>

      {/* Registration code link */}
      <p className="text-xs text-gray-500 text-center mb-12">
        Have a Scrivar registration code on a card?{' '}
        <Link to="/activate" className="text-gray-700 hover:text-gray-900 underline">
          Enter code here
        </Link>
      </p>

      {/* Footer */}
      <p className="text-xs text-gray-400 text-center select-none">
        Scrivar &middot; by Scrivar Inc.
      </p>
    </div>
  );
}
